"use client";

import clsx from "clsx";
import React from "react";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { markdownTableComponents } from "@/components/ask/MarkdownTableWithCopy";
import { normalizeMathDelimiters } from "@/utils/common/math";
import {
  createRehypeSanitizeSchema,
  getSafeHref,
  isUrlSafe,
} from "@/utils/common/security";

const sanitizeSchema = createRehypeSanitizeSchema(defaultSchema);

/**
 * Simple link renderer that opens links in a new tab with security checks
 */
function LinkRenderer(props: any) {
  const children = props.children;

  // Security: Validate and sanitize the URL to prevent javascript: and data: URLs
  const safeHref = getSafeHref(props.href);

  // If URL is not safe, render as plain text instead of a clickable link
  if (!isUrlSafe(props.href)) {
    return <span>{children}</span>;
  }

  return (
    <a href={safeHref} target="_blank" rel="noopener noreferrer">
      {children}
      <Icon
        icon="arrow-up-right-from-square"
        size={ICON_SIZES.XXS}
        className="ml-1"
      />
    </a>
  );
}

export type MarkdownRendererProps = {
  /** The markdown content to render */
  content: string;
  /** Additional CSS classes to apply */
  className?: string;
  /** Whether to allow images (default: false for security) */
  allowImages?: boolean;
};

/**
 * A reusable markdown renderer component with security features.
 * Uses react-markdown with GitHub Flavored Markdown support.
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  allowImages = false,
}) => {
  const normalizedContent = React.useMemo(
    () => normalizeMathDelimiters(content),
    [content]
  );
  return (
    <Markdown
      className={clsx(
        // Link styles
        "[&_a]:underline",
        "[&_a]:text-primary",
        // Base class for markdown content
        "mrkdwn",
        // Table styles
        "[&_table]:border-collapse",
        "[&_table]:border",
        "[&_table]:border-300",
        "[&_table]:my-4",
        "[&_table]:w-max",
        "[&_th]:border",
        "[&_th]:border-300",
        "[&_th]:bg-background",
        "[&_th]:px-4",
        "[&_th]:py-2",
        "[&_th]:text-left",
        "[&_th]:font-semibold",
        "[&_td]:border",
        "[&_td]:border-300",
        "[&_td]:px-4",
        "[&_td]:py-2",
        // Heading styles
        "[&_h1]:text-2xl",
        "[&_h1]:font-bold",
        "[&_h1]:mb-4",
        "[&_h1]:mt-6",
        "[&_h2]:text-xl",
        "[&_h2]:font-semibold",
        "[&_h2]:mb-3",
        "[&_h2]:mt-5",
        "[&_h3]:text-lg",
        "[&_h3]:font-medium",
        "[&_h3]:mb-2",
        "[&_h3]:mt-4",
        "[&_h4]:text-base",
        "[&_h4]:font-medium",
        "[&_h4]:mb-2",
        "[&_h4]:mt-3",
        // Paragraph styles
        "[&_p]:mb-3",
        "[&_p]:leading-relaxed",
        // List styles
        "[&_ul]:list-disc",
        "[&_ul]:pl-6",
        "[&_ul]:mb-3",
        "[&_ol]:list-decimal",
        "[&_ol]:pl-6",
        "[&_ol]:mb-3",
        "[&_li]:mb-1",
        // Code styles
        "[&_code]:bg-muted",
        "[&_code]:px-1.5",
        "[&_code]:py-0.5",
        "[&_code]:rounded",
        "[&_code]:text-sm",
        "[&_code]:font-mono",
        "[&_pre]:bg-muted",
        "[&_pre]:p-4",
        "[&_pre]:rounded-lg",
        "[&_pre]:overflow-x-auto",
        "[&_pre]:mb-4",
        // Blockquote styles
        "[&_blockquote]:border-l-4",
        "[&_blockquote]:border-primary",
        "[&_blockquote]:pl-4",
        "[&_blockquote]:italic",
        "[&_blockquote]:my-4",
        // Horizontal rule
        "[&_hr]:my-6",
        "[&_hr]:border-300",
        // Custom class
        className
      )}
      components={{
        a: LinkRenderer,
        // Security: Block image rendering by default to prevent tracking pixels
        img: allowImages ? undefined : () => null,
        ...markdownTableComponents,
      }}
      remarkPlugins={[remarkMath, remarkGfm]}
      rehypePlugins={[rehypeKatex, [rehypeSanitize, sanitizeSchema]]}
    >
      {normalizedContent}
    </Markdown>
  );
};

export default MarkdownRenderer;
